import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRegion } from '../context/RegionContext';
import { fetchRegions } from '../services/api';
import ChatWidget from './ChatWidget'; // Import the chat widget
import { useLocation } from 'react-router-dom';
import './Layout.css';

function Layout({ children }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const { pathname } = useLocation();
    const location = useLocation();
    const navigate = useNavigate(); 

    // Region state
    const [availableRegions, setAvailableRegions] = useState([]);
    const [regionsLoading, setRegionsLoading] = useState(true);
    const { selectedRegion, updateRegion, allowedRegions, canAccessRegion } = useRegion();

    // Load regions on component mount
    useEffect(() => {
        loadRegions();
    }, []);

    // Collapsable Sidebar
    const toggleSidebar = () => {
        setSidebarCollapsed(prev => !prev);
    };

    // Auto-redirect from Bunnings page if not in AU region
    useEffect(() => {
        if (location.pathname === '/bunnings-routes' && selectedRegion !== 'vquest-au-1') {
            navigate('/dashboard');
            alert('Bunnings integration is only available for the Australia region.');
        }
    }, [selectedRegion, location.pathname, navigate]);

    const loadRegions = async () => {
        try {
            const regions = await fetchRegions();
            setAvailableRegions(regions);
        } catch (error) {
            console.error('❌ Error loading regions:', error);
            // Fallback regions
            setAvailableRegions([
                { id: 'vquest', name: 'New Zealand' },
                { id: 'vquest-au-1', name: 'Australia' },
                { id: 'vquest-irl-1', name: 'Ireland' },
                { id: 'vquest-us-1', name: 'USA/Canada' },
                { id: 'vquest-midrise', name: 'Mid Rise' }
            ]);
        } finally {
            setRegionsLoading(false);
        }
    };

    // Handle region change
    const handleRegionChange = (e) => {
        const newRegionId = e.target.value;
        
        // Check if user has access to this region
        if (allowedRegions.length > 0 && !canAccessRegion(newRegionId)) {
            alert(`Access denied to region: ${newRegionId}. Contact your administrator for access.`);
            return;
        }
        
        updateRegion(newRegionId);
        console.log('🌏 Region switched to:', newRegionId);

        // Force page reload to refresh all data across all pages
        setTimeout(() => {
            window.location.reload();
        }, 100); // Small delay to ensure region is saved to localStorage
    };

    // Get user's accessible regions
    const getUserAccessibleRegions = () => {
        if (allowedRegions.length === 0) {
            // If no restrictions, show all regions
            return availableRegions;
        }
        
        // Filter regions based on user's allowed regions
        return availableRegions.filter(region => allowedRegions.includes(region.id));
    };

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const toggleDropdown = (menu) => {
        setActiveDropdown(prev => (prev === menu ? null : menu));
    };

    return (
        <div className="layout">
            {/* ✅ UPDATED: Add collapsed class conditionally */}
            <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                {/* ✅ NEW: Collapse toggle button */}
                <button className="sidebar-toggle" onClick={toggleSidebar} title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
                    {sidebarCollapsed ? '▶' : '◀'}
                </button>

                <div className="logo">
                    <Link to="/dashboard">
                        {/* ✅ NEW: Different logos for collapsed/expanded */}
                        {sidebarCollapsed ? (
                            <img src="/icons/Nezo_White_Fabricon.png" alt="Logo" className="logo-icon" />
                        ) : (
                            <img src="/icons/Nezo_Logo_White.png" alt="Logo" className="logo-full" />
                        )}
                    </Link>
                </div>
                <hr />

                {/* ✅ UPDATED: All menu items with conditional text display */}
                <Link to="/dashboard" className="menu-item" title="Dashboard">
                    <img src="/icons/computer.png" alt="Dashboard Icon" />
                    {!sidebarCollapsed && <span className="menu-text">Dashboard</span>}
                </Link>
                <hr />

                {/* API Section */}
                <div className="menu-item" onClick={() => toggleDropdown('api')} title="API's">
                    <img src="/icons/connector.png" alt="API Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">API's</span>
                            <span className="chevron">{activeDropdown === 'api' ? '▲' : '▼'}</span>
                        </>
                    )}
                </div>
                {!sidebarCollapsed && activeDropdown === 'api' && (
                    <div className="submenu">
                        <Link to="/EC3data" className="submenu-item">EC3 Data</Link>
                        {selectedRegion === 'vquest-au-1' && (
                            <Link to="/bunnings-routes" className="submenu-item">
                                Bunnings Data<span className="region-badge-au">AU</span>
                            </Link>
                        )}
                        {selectedRegion === 'vquest' && (
                            <Link to="/placemakers" className="submenu-item">
                                Placemakers Data<span className="region-badge-au">NZ</span>
                            </Link>
                        )}
                    </div>
                )}

                {/* Materials Section */}
                <Link to="/categories" className="menu-item" onClick={() => toggleDropdown('builder')} title="Materials">
                    <img src="/icons/components.png" alt="Materials Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">Materials</span>
                            <span className="chevron">{activeDropdown === 'builder' ? '▲' : '▼'}</span>
                        </>
                    )}
                </Link>
                {!sidebarCollapsed && activeDropdown === 'builder' && (
                    <div className="submenu">
                        <Link to="/newmaterial" className="submenu-item">New Material</Link>
                        <Link to="/newbreakdown" className="submenu-item">New Breakdown</Link>
                        <Link to="/materialeditor" className="submenu-item">Material Editor</Link>
                        <Link to="/newcategory" className="submenu-item">New Categories</Link>
                        <Link to="/managecategory" className="submenu-item">Manage Categories</Link>
                    </div>
                )}

                {/* Cost Section */}
                <Link to="/supply-rates" className="menu-item" onClick={() => toggleDropdown('cost')} title="Cost">
                    <img src="/icons/dollar.png" alt="Cost Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">Cost</span>
                            <span className="chevron">{activeDropdown === 'cost' ? '▲' : '▼'}</span>
                        </>
                    )}
                </Link>
                {!sidebarCollapsed && activeDropdown === 'cost' && (
                    <div className="submenu">
                        <Link to="/newcost" className="submenu-item">New Cost</Link>
                        <Link to="/editcost" className="submenu-item">Edit Cost</Link>
                        <Link to="/cost-editor" className="submenu-item">Cost Editor</Link>
                    </div>
                )}

                {/* CO2/Weight Section */}
                <Link to="/co2" className="menu-item" onClick={() => toggleDropdown('co2')} title="CO2 / Weight">
                    <img src="/icons/leaf.png" alt="CO2 Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">Co2 / Weight</span>
                            <span className="chevron">{activeDropdown === 'co2' ? '▲' : '▼'}</span>
                        </>
                    )}
                </Link>
                {!sidebarCollapsed && activeDropdown === 'co2' && (
                    <div className="submenu">
                        <Link to="/newCo2Weight" className="submenu-item">New Co2 / Weight</Link>
                        <Link to="/editco2weight" className="submenu-item">Edit Co2 / Weight</Link>
                        {selectedRegion === 'vquest-au-1' && (
                            <>
                                <Link to="/nabers-epd-categories" className="submenu-item">NABERS EPD View</Link>
                                <Link to="/nabers-ref-ef" className="submenu-item">NABERS Ref EF</Link>
                            </>
                        )}
                    </div>
                )}

                {/* Products Section */}
                <div className="menu-item" onClick={() => toggleDropdown('materials')} title="Products">
                    <img src="/icons/list.png" alt="Products Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">Products</span>
                            <span className="chevron">{activeDropdown === 'materials' ? '▲' : '▼'}</span>
                        </>
                    )}
                </div>
                {!sidebarCollapsed && activeDropdown === 'materials' && (
                    <div className="submenu">
                        <Link to="/newproduct" className="submenu-item">New Product</Link>
                    </div>
                )}

                {/* Account */}
                <Link to="/account" className="menu-item" title="Account">
                    <img src="/icons/person.png" alt="Account Icon" />
                    {!sidebarCollapsed && <span className="menu-text">Account</span>}
                </Link>

                {/* Settings Section */}
                <Link className="menu-item" onClick={() => toggleDropdown('settings')} title="Settings">
                    <img src="/icons/settings.png" alt="Settings Icon" />
                    {!sidebarCollapsed && (
                        <>
                            <span className="menu-text">Settings</span>
                            <span className="chevron">{activeDropdown === 'settings' ? '▲' : '▼'}</span>
                        </>
                    )}
                </Link>
                {!sidebarCollapsed && activeDropdown === 'settings' && (
                    <div className="submenu">
                        <Link to="/logs" className="submenu-item">Logs</Link>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Top Bar */}
                <div className="top-bar">
                    <div className="search-container">
                        <input
                            className="search-bar"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyPress}
                        />
                        <button className="search-btn" onClick={handleSearch}>
                            Search
                        </button>
                    </div>
                    <div className="top-buttons">
                        {/* Region Dropdown */}
                        <div className="region-dropdown-container">
                            {regionsLoading ? (
                                <div className="region-loading">🌏</div>
                            ) : (
                                <select 
                                    value={selectedRegion || ''} 
                                    onChange={handleRegionChange}
                                    className="region-dropdown"
                                    title="Switch Region"
                                >
                                    <option value="" disabled>Select Region</option>
                                    {getUserAccessibleRegions().map((region) => (
                                        <option key={region.id} value={region.id}>
                                            🌏 {region.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        
                        <button className="top-btn" title="Notifications">🔔</button>
                        {/* ✅ FIX: Use Link directly instead of nesting Link inside button */}
                        <Link to="/messages" className="top-btn" title="Messages">✉️</Link>
                        <Link to="/account" className="top-btn" title="Account">👤</Link>
                    </div>
                </div>

                {/* Page Content */}
                <div className="content-container">{children}</div>
            </div>

            {/* 🆕 Chat Widget - only show when not on messages page */}
            {pathname !== '/messages' && <ChatWidget />}
        </div>
    );
}

export default Layout;